import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiArrowLeft, FiChevronRight, FiHome, FiSettings} = FiIcons;

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb Navigation */}
      <div className="bg-slate-50 border-b border-slate-200 pt-24 pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center space-x-2 text-sm">
            <Link 
              to="/" 
              className="flex items-center space-x-1 text-slate-500 hover:text-orange-600 transition-colors duration-300"
            >
              <SafeIcon icon={FiHome} className="text-xs" />
              <span>Home</span>
            </Link>
            <SafeIcon icon={FiChevronRight} className="text-slate-400 text-xs" />
            <span className="text-slate-900 font-medium">Cookie Policy</span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-orange-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            className="text-white"
          >
            <SafeIcon icon={FiSettings} className="text-4xl text-orange-400 mb-6 mx-auto" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Cookie Policy
            </h1>
            <p className="text-xl text-slate-300">
              Last updated: January 2025
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8, delay: 0.2}}
            className="prose prose-lg max-w-none"
          >
            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-lg mb-8">
              <p className="text-slate-700 mb-0">
                This Cookie Policy explains how WeDesignedIt uses cookies and similar technologies 
                on our website to enhance your browsing experience and improve our services.
              </p>
            </div>

            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">1. What Are Cookies?</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  Cookies are small text files that are stored on your device when you visit a website. 
                  They help websites remember your preferences and improve your browsing experience. 
                  Cookies contain information about your visit, such as your preferred language and other settings.
                </p>
                <p className="text-slate-700 leading-relaxed mb-4">
                  Similar technologies include web beacons, pixel tags, and local storage, which serve similar 
                  functions to cookies.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">2. How We Use Cookies</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  We use cookies for various purposes to enhance your experience on our website:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Remembering your preferences and settings</li>
                  <li>Analyzing website traffic and user behavior</li>
                  <li>Improving website functionality and performance</li>
                  <li>Personalizing content and advertisements</li>
                  <li>Enabling social media features</li>
                  <li>Preventing fraud and enhancing security</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Types of Cookies We Use</h2>
                
                <div className="space-y-6">
                  <div className="bg-slate-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">Essential Cookies</h3>
                    <p className="text-slate-700 leading-relaxed mb-3">
                      These cookies are necessary for the website to function properly. They enable core functionality 
                      such as security, network management, and accessibility.
                    </p>
                    <ul className="list-disc pl-6 text-slate-700 space-y-1">
                      <li>Session management</li>
                      <li>Security and authentication</li>
                      <li>Load balancing</li>
                      <li>Cookie consent preferences</li>
                    </ul>
                  </div>

                  <div className="bg-slate-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">Performance Cookies</h3>
                    <p className="text-slate-700 leading-relaxed mb-3">
                      These cookies collect information about how you use our website, helping us improve 
                      performance and user experience.
                    </p>
                    <ul className="list-disc pl-6 text-slate-700 space-y-1">
                      <li>Google Analytics</li>
                      <li>Page load times</li>
                      <li>Error tracking</li>
                      <li>User journey analysis</li>
                    </ul>
                  </div>

                  <div className="bg-slate-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">Functional Cookies</h3>
                    <p className="text-slate-700 leading-relaxed mb-3">
                      These cookies enable enhanced functionality and personalization, such as remembering 
                      your preferences and providing customized content.
                    </p>
                    <ul className="list-disc pl-6 text-slate-700 space-y-1">
                      <li>Language preferences</li>
                      <li>Region selection</li>
                      <li>Form auto-completion</li>
                      <li>Chat widget functionality</li>
                    </ul>
                  </div>

                  <div className="bg-slate-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">Marketing Cookies</h3>
                    <p className="text-slate-700 leading-relaxed mb-3">
                      These cookies track your browsing habits to deliver personalized advertisements 
                      and measure the effectiveness of marketing campaigns.
                    </p>
                    <ul className="list-disc pl-6 text-slate-700 space-y-1">
                      <li>Google Ads</li>
                      <li>Facebook Pixel</li>
                      <li>LinkedIn Insights</li>
                      <li>Retargeting campaigns</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Third-Party Cookies</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  We use several third-party services that may set their own cookies:
                </p>
                
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-slate-300 bg-white">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="border border-slate-300 px-4 py-3 text-left font-semibold">Service</th>
                        <th className="border border-slate-300 px-4 py-3 text-left font-semibold">Purpose</th>
                        <th className="border border-slate-300 px-4 py-3 text-left font-semibold">Privacy Policy</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-slate-300 px-4 py-3">Google Analytics</td>
                        <td className="border border-slate-300 px-4 py-3">Website analytics and performance tracking</td>
                        <td className="border border-slate-300 px-4 py-3">
                          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" 
                             className="text-orange-600 hover:text-orange-700">Google Privacy Policy</a>
                        </td>
                      </tr>
                      <tr>
                        <td className="border border-slate-300 px-4 py-3">Google Ads</td>
                        <td className="border border-slate-300 px-4 py-3">Advertising and conversion tracking</td>
                        <td className="border border-slate-300 px-4 py-3">
                          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" 
                             className="text-orange-600 hover:text-orange-700">Google Privacy Policy</a>
                        </td>
                      </tr>
                      <tr>
                        <td className="border border-slate-300 px-4 py-3">Facebook Pixel</td>
                        <td className="border border-slate-300 px-4 py-3">Social media advertising and tracking</td>
                        <td className="border border-slate-300 px-4 py-3">
                          <a href="https://www.facebook.com/privacy/explanation" target="_blank" rel="noopener noreferrer" 
                             className="text-orange-600 hover:text-orange-700">Facebook Privacy Policy</a>
                        </td>
                      </tr>
                      <tr>
                        <td className="border border-slate-300 px-4 py-3">LinkedIn Insights</td>
                        <td className="border border-slate-300 px-4 py-3">Professional networking and B2B marketing</td>
                        <td className="border border-slate-300 px-4 py-3">
                          <a href="https://www.linkedin.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" 
                             className="text-orange-600 hover:text-orange-700">LinkedIn Privacy Policy</a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Managing Your Cookie Preferences</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  You have several options for managing cookies:
                </p>

                <h3 className="text-xl font-semibold text-slate-900 mb-3">Browser Settings</h3>
                <p className="text-slate-700 leading-relaxed mb-4">
                  Most browsers allow you to control cookies through their settings. You can:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-6">
                  <li>Block all cookies</li>
                  <li>Block third-party cookies only</li>
                  <li>Delete existing cookies</li>
                  <li>Set cookies to expire when you close your browser</li>
                  <li>Be notified when cookies are set</li>
                </ul>

                <h3 className="text-xl font-semibold text-slate-900 mb-3">Browser-Specific Instructions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-slate-900 mb-2">Chrome</h4>
                    <p className="text-sm text-slate-700">Settings → Privacy and Security → Cookies and other site data</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-slate-900 mb-2">Firefox</h4>
                    <p className="text-sm text-slate-700">Options → Privacy & Security → Cookies and Site Data</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-slate-900 mb-2">Safari</h4>
                    <p className="text-sm text-slate-700">Preferences → Privacy → Manage Website Data</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-slate-900 mb-2">Edge</h4>
                    <p className="text-sm text-slate-700">Settings → Cookies and site permissions → Cookies and site data</p>
                  </div>
                </div>

                <h3 className="text-xl font-semibold text-slate-900 mb-3">Opt-Out Tools</h3>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>
                    <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" 
                       className="text-orange-600 hover:text-orange-700">Google Analytics Opt-out Browser Add-on</a>
                  </li>
                  <li>
                    <a href="https://www.youronlinechoices.com/uk/" target="_blank" rel="noopener noreferrer" 
                       className="text-orange-600 hover:text-orange-700">Your Online Choices (UK)</a>
                  </li>
                  <li>
                    <a href="https://optout.networkadvertising.org/" target="_blank" rel="noopener noreferrer" 
                       className="text-orange-600 hover:text-orange-700">Network Advertising Initiative Opt-out</a>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Cookie Consent</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  When you first visit our website, we'll ask for your consent to use non-essential cookies. 
                  You can:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Accept all cookies</li>
                  <li>Accept only essential cookies</li>
                  <li>Customize your cookie preferences</li>
                  <li>Change your preferences at any time through our cookie settings</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Impact of Disabling Cookies</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  Disabling cookies may affect your experience on our website:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Some website features may not function properly</li>
                  <li>You may need to re-enter information repeatedly</li>
                  <li>Personalized content and recommendations may not be available</li>
                  <li>We may not be able to remember your preferences</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Updates to This Policy</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  We may update this Cookie Policy to reflect changes in our practices or for legal reasons. 
                  We'll notify you of significant changes through our website or other communication methods.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Contact Information</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  If you have questions about our use of cookies, please contact us:
                </p>
                <div className="bg-slate-50 p-6 rounded-lg">
                  {/* <p className="text-slate-700 mb-2">
                    <strong>WeDesignedIt</strong><br />
                    59 Walton St, Aylesbury HP21 7QG<br />
                    United Kingdom
                  </p> */}
                  <p className="text-slate-700">
                    Email: <a href="mailto:privacy@wedesignedit.co.uk" className="text-orange-600 hover:text-orange-700">privacy@wedesignedit.co.uk</a><br />
                    {/* Phone: <a href="tel:+447769061291" className="text-orange-600 hover:text-orange-700">07769061291</a> */}
                  </p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-8 text-center">Related Legal Documents</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link 
              to="/terms-of-service" 
              className="block bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
            >
              <h4 className="text-lg font-semibold text-slate-900 mb-2">Terms of Service</h4>
              <p className="text-slate-600">Read our terms and conditions for using our services.</p>
            </Link>
            <Link 
              to="/privacy-policy" 
              className="block bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
            >
              <h4 className="text-lg font-semibold text-slate-900 mb-2">Privacy Policy</h4>
              <p className="text-slate-600">Learn how we collect, use, and protect your personal information.</p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicy;